import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Radar, Copy, Mail, RefreshCw, Ban, ExternalLink } from "lucide-react";
import { toast } from "sonner";

export function TrackerPanel({ job, canEdit, onEmail }) {
  const [link, setLink] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => api.get(`/jobs/${job.id}/tracker-link`).then((r) => setLink(r.data)).catch(() => {});
  useEffect(() => { load(); }, [job.id]);
  if (!canEdit) return null;

  const run = async (fn, msg) => { setBusy(true); try { await fn(); if (msg) toast.success(msg); await load(); } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } finally { setBusy(false); } };
  const copy = () => navigator.clipboard.writeText(link.url).then(() => toast.success("Tracker link copied"));

  return (
    <div className="rounded-lg border border-[#0b1f3a]/15 bg-white p-3 space-y-2" data-testid="tracker-panel">
      <div className="flex items-center gap-2 text-sm font-medium text-[#0b1f3a]"><Radar className="h-4 w-4" />Client Tracker</div>
      {link?.url ? (
        <>
          <a href={link.url} target="_blank" rel="noreferrer" className="block text-xs font-mono text-[#1f5fa8] truncate hover:underline" data-testid="tracker-link-url">{link.url.replace(/^https?:\/\//, "")}</a>
          <div className="text-[11px] text-slate-500">{link.views} view{link.views === 1 ? "" : "s"} · anyone with the link can see status, no login</div>
          <div className="flex flex-wrap gap-1.5">
            <Button size="sm" variant="outline" className="h-7 text-xs" data-testid="tracker-copy" onClick={copy}><Copy className="h-3 w-3 mr-1" />Copy</Button>
            <Button size="sm" variant="outline" className="h-7 text-xs" data-testid="tracker-email" disabled={busy} onClick={onEmail}><Mail className="h-3 w-3 mr-1" />Email client</Button>
            <a href={link.url} target="_blank" rel="noreferrer"><Button size="sm" variant="outline" className="h-7 text-xs" data-testid="tracker-open"><ExternalLink className="h-3 w-3 mr-1" />Open</Button></a>
            <Button size="sm" variant="ghost" className="h-7 text-xs" data-testid="tracker-regenerate" disabled={busy} onClick={() => { if (window.confirm("Regenerate? The old link will stop working.")) run(() => api.post(`/jobs/${job.id}/tracker-link`), "New link generated"); }}><RefreshCw className="h-3 w-3 mr-1" />Regenerate</Button>
            <Button size="sm" variant="ghost" className="h-7 text-xs text-rose-600" data-testid="tracker-revoke" disabled={busy} onClick={() => { if (window.confirm("Revoke the tracker link? The client will no longer be able to view it.")) run(() => api.delete(`/jobs/${job.id}/tracker-link`), "Link revoked"); }}><Ban className="h-3 w-3 mr-1" />Revoke</Button>
          </div>
        </>
      ) : (
        <>
          <div className="text-xs text-slate-500">Give your client a live, pizza-tracker-style status page so they stop calling for updates.</div>
          <Button size="sm" className="sm-green h-7 text-xs" data-testid="tracker-generate" disabled={busy} onClick={() => run(() => api.post(`/jobs/${job.id}/tracker-link`), "Tracker link created")}><Radar className="h-3 w-3 mr-1" />Generate tracker link</Button>
        </>
      )}
    </div>
  );
}
